import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { api } from '../api';
import { ROLLEN_LABEL, useAuth } from '../auth';
import type { Rolle } from '../types';

interface Punkt {
  label: string;
  pfad?: string;
  /** Kommt in dieser Phase - wird angezeigt, ist aber noch nicht anklickbar. */
  phase?: number;
  rollen?: Rolle[];
}

interface Gruppe {
  titel?: string;
  rollen?: Rolle[];
  punkte: Punkt[];
}

const LADEN: Rolle[] = ['admin', 'verkauf'];

/**
 * Der Menübaum aus dem Konzept. Noch nicht gebaute Bereiche stehen grau mit
 * ihrer Phase drin, damit die Struktur von Anfang an sichtbar ist.
 */
const MENUE: Gruppe[] = [
  {
    punkte: [
      { label: 'Mein Tag', pfad: '/' },
      { label: 'Tickets', pfad: '/tickets' },
      { label: 'Kunden', pfad: '/kunden' },
    ],
  },
  {
    titel: 'Bestellungen',
    rollen: ['admin', 'verkauf', 'techniker'],
    punkte: [
      { label: 'Übersicht', pfad: '/bestellungen' },
      { label: 'Neue Bestellung', pfad: '/bestellungen/neu', rollen: LADEN },
    ],
  },
  {
    titel: 'Lieferungen',
    rollen: ['admin', 'verkauf', 'fahrer'],
    punkte: [
      { label: 'Übersicht', pfad: '/lieferungen' },
      { label: 'Neue Lieferung', pfad: '/lieferungen/neu', rollen: LADEN },
    ],
  },
  {
    titel: 'Einsätze',
    punkte: [
      { label: 'Kalender & Touren', pfad: '/einsaetze' },
    ],
  },
  {
    titel: 'Service',
    rollen: ['admin', 'verkauf', 'techniker'],
    punkte: [
      { label: 'Werkstatt', pfad: '/service' },
      { label: 'Gerät annehmen', pfad: '/service/neu' },
      { label: 'Außendienst', pfad: '/service/aussendienst' },
    ],
  },
  { titel: 'Medimax', rollen: LADEN, punkte: [{ label: 'Aufträge', phase: 5 }, { label: 'Terminfenster', phase: 5 }, { label: 'Monatsabrechnung', phase: 5 }] },
  {
    titel: 'Kasse & Forderungen',
    rollen: LADEN,
    punkte: [
      { label: 'Forderungen', pfad: '/forderungen' },
      { label: 'Bargeldabgabe', pfad: '/kasse/bargeld' },
    ],
  },
  { titel: 'Telefon', rollen: LADEN, punkte: [{ label: 'Anrufe', phase: 6 }, { label: 'Telefonprotokolle', phase: 7 }] },
  { titel: 'Beratung', rollen: LADEN, punkte: [{ label: 'Mobilfunk-Beratung', phase: 7 }, { label: 'Leihscheine', phase: 7 }] },
  { titel: 'Wissen & Zahlen', punkte: [{ label: 'Wissensdatenbank', phase: 7 }, { label: 'KPI-Dashboard', phase: 7, rollen: LADEN }] },
  {
    titel: 'Einstellungen',
    punkte: [
      { label: 'Status & Fristen', pfad: '/einstellungen/fristen', rollen: ['admin'] },
      { label: 'Eskalation & Mails', pfad: '/einstellungen/eskalation', rollen: ['admin'] },
      { label: 'Benutzer & Rollen', pfad: '/einstellungen/benutzer', rollen: ['admin'] },
      { label: 'Mein Passwort', pfad: '/passwort' },
    ],
  },
];

export function Layout() {
  const { user, logout } = useAuth();
  const [offen, setOffen] = useState(false);
  const [ueberfaellig, setUeberfaellig] = useState(0);
  const ort = useLocation();

  useEffect(() => setOffen(false), [ort.pathname]);

  // Die Zahl am "Mein Tag" ist der Stachel, der im Altsystem fehlte.
  useEffect(() => {
    const laden = () =>
      api<{ ueberfaellig: unknown[] }>('/api/mein-tag')
        .then((r) => setUeberfaellig(r.ueberfaellig.length))
        .catch(() => undefined);
    void laden();
    const takt = window.setInterval(laden, 60_000);
    return () => window.clearInterval(takt);
  }, [ort.pathname]);

  if (!user) return null;
  const darf = (rollen?: Rolle[]) => !rollen || rollen.includes(user.rolle);

  return (
    <div className="shell">
      <div className="mobil-kopf">
        <button onClick={() => setOffen(true)} aria-label="Menü">
          ☰
        </button>
        <strong>OrderFlow</strong>
      </div>
      <aside className={offen ? 'sidebar offen' : 'sidebar'}>
        <div className="marke">
          <img src="/kg-media.svg" alt="" />
          OrderFlow
        </div>
        <nav>
          {MENUE.filter((g) => darf(g.rollen)).map((g, i) => {
            const punkte = g.punkte.filter((p) => darf(p.rollen));
            if (punkte.length === 0) return null;
            return (
              <div key={i}>
                {g.titel && <div className="gruppe">{g.titel}</div>}
                {punkte.map((p) =>
                  p.pfad ? (
                    <NavLink key={p.label} to={p.pfad} end>
                      {p.label}
                      {p.pfad === '/' && ueberfaellig > 0 && <span className="zaehler" title="überfällig">{ueberfaellig}</span>}
                    </NavLink>
                  ) : (
                    <span key={p.label} className="bald" title={`Kommt in Phase ${p.phase}`}>
                      {p.label}
                      <small>Ph. {p.phase}</small>
                    </span>
                  ),
                )}
              </div>
            );
          })}
        </nav>
        <div className="fuss">
          <div>{user.name}</div>
          <div className="small" style={{ color: 'var(--sidebar-soft)' }}>
            {ROLLEN_LABEL[user.rolle]}
          </div>
          <button onClick={logout} style={{ marginTop: 8 }}>
            Abmelden
          </button>
        </div>
      </aside>
      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}
